import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider,
  Alert,
  LinearProgress
} from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalLaundryServiceOutlinedIcon from '@mui/icons-material/LocalLaundryServiceOutlined';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import SignalWifi4BarIcon from '@mui/icons-material/SignalWifi4Bar';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningIcon from '@mui/icons-material/Warning';
import * as d3 from 'd3';
import { getDispenserByName, generatePaperTowelMockData } from '../../data/paperTowelMockData';

const PaperTowelEntityDrawer = ({ entityDetails, chartRef }) => {
  const [selectedDispenser, setSelectedDispenser] = useState(null);
  const [allDispensersData, setAllDispensersData] = useState(null);

  useEffect(() => {
    // Generate all dispensers data
    const dispensersData = generatePaperTowelMockData();
    setAllDispensersData(dispensersData);

    let foundDispenser = null;

    // Find the specific dispenser based on entityDetails name
    if (entityDetails && entityDetails.name) {
      foundDispenser = dispensersData.dispensers.find(dispenser => dispenser.name === entityDetails.name);
      
      // If not found by exact name, try MAC address from keys as fallback
      if (!foundDispenser && entityDetails.keys) {
        const macAddressKey = entityDetails.keys.find(key => key.keyName === 'mac_address');
        if (macAddressKey) {
          foundDispenser = getDispenserByName(macAddressKey.value);
        }
      }
    }
    
    // Fallback to first dispenser if no specific dispenser found
    if (!foundDispenser && dispensersData.dispensers.length > 0) {
      foundDispenser = dispensersData.dispensers[0];
    }

    setSelectedDispenser(foundDispenser);
  }, [entityDetails]);

  const renderConsumptionBarChart = useCallback(() => {
    if (!selectedDispenser || !chartRef.current) return;
    d3.select(chartRef.current).selectAll('*').remove();

    const width = 400;
    const height = 300;
    const margin = { top: 20, right: 20, bottom: 60, left: 60 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    const svg = d3.select(chartRef.current)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    const data = selectedDispenser.consumption7d;

    const xScale = d3.scaleBand()
      .domain(data.map(d => d.day))
      .range([0, chartWidth])
      .padding(0.2);

    const yScale = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.used)])
      .nice()
      .range([chartHeight, 0]);

    svg.append('g')
      .attr('transform', `translate(0, ${chartHeight})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.8em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(-45)')
      .style('font-size', '10px');

    svg.append('g')
      .call(d3.axisLeft(yScale))
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -40)
      .attr('x', -chartHeight / 2)
      .attr('text-anchor', 'middle')
      .text('Units Used')
      .style('font-size', '12px');

    const bars = svg.selectAll('.bar')
      .data(data)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', d => xScale(d.day))
      .attr('y', d => yScale(d.used))
      .attr('width', xScale.bandwidth())
      .attr('height', d => chartHeight - yScale(d.used))
      .attr('fill', '#2196F3')
      .style('cursor', 'pointer');

    // Simple tooltip
    const tooltip = d3.select(chartRef.current)
      .append('div')
      .attr('class', 'tooltip')
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('background', 'rgba(0, 0, 0, 0.8)')
      .style('color', 'white')
      .style('padding', '8px')
      .style('border-radius', '4px')
      .style('font-size', '12px')
      .style('pointer-events', 'none');

    bars.on('mouseover', function(event, d) {
      d3.select(this).attr('fill', '#1976D2');
      tooltip.transition().duration(200).style('opacity', 0.9);
      tooltip.html(`<strong>${d.day}</strong><br/>Used: ${d.used}`)
        .style('left', (event.pageX + 10) + 'px')
        .style('top', (event.pageY - 28) + 'px');
    }).on('mouseout', function() {
      d3.select(this).attr('fill', '#2196F3');
      tooltip.transition().duration(300).style('opacity', 0);
    });

    svg.append('text')
      .attr('x', chartWidth / 2)
      .attr('y', -5)
      .attr('text-anchor', 'middle')
      .style('font-size', '14px')
      .style('font-weight', 'bold')
      .text('Paper Towel Consumption - Last 7 Days');
  }, [selectedDispenser]);

  useEffect(() => {
    if (chartRef.current && selectedDispenser && selectedDispenser.consumption7d.length > 0) {
      renderConsumptionBarChart();
    }
  }, [selectedDispenser, renderConsumptionBarChart]);

  // Show loading state if no dispenser data
  if (!selectedDispenser || !allDispensersData) {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Loading paper towel dispenser data...
        </Typography>
      </Box>
    );
  }

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Empty': return 'error';
      case 'Low': return 'warning';
      case 'OK': return 'success';
      default: return 'primary';
    }
  };

  return (
    <>
      {/* Paper Towel Dispenser Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center">
              <Inventory2OutlinedIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Box>
                <Typography variant="h6">
                  {selectedDispenser.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedDispenser.location}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap={1}>
              <Chip 
                label={selectedDispenser.status} 
                color={getStatusColor(selectedDispenser.status)}
                variant="outlined" 
              />
              <Chip 
                label={selectedDispenser.bathroomType} 
                color="primary"
                variant="outlined" 
              />
            </Box>
          </Box>

          {/* Status Alert */}
          {(selectedDispenser.status === 'Empty' || selectedDispenser.status === 'Low') && (
            <Alert severity={selectedDispenser.status === 'Empty' ? 'error' : 'warning'} sx={{ mb: 2 }}>
              {selectedDispenser.status === 'Empty' && 'Dispenser is empty and needs immediate refill.'}
              {selectedDispenser.status === 'Low' && 'Dispenser is running low and should be refilled soon.'}
            </Alert>
          )}
          
          {/* Dispenser Metrics Summary */}
          <Divider sx={{ my: 2 }} />
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Dispenser Status Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Box textAlign="center">
                  <Typography 
                    variant="h6" 
                    color={selectedDispenser.currentLevelPercent <= 10 ? 'error.main' : 
                           selectedDispenser.currentLevelPercent <= 25 ? 'warning.main' : 'success.main'}
                  >
                    {selectedDispenser.currentLevelPercent}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Current Level
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box textAlign="center">
                  <Typography variant="h6" color="primary">
                    {selectedDispenser.currentTowels}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Towels Left
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box textAlign="center">
                  <Typography variant="h6" color="info.main">
                    {selectedDispenser.metrics.dailyUsageAvg}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Daily Usage
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box textAlign="center">
                  <Typography variant="h6" color="secondary.main">
                    {selectedDispenser.metrics.refillFrequency}d
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Refill Cycle
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Dispenser Details */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Dispenser Details
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <AccessTimeIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Last Refilled" 
                secondary={new Date(selectedDispenser.lastRefilledAt).toLocaleString()} 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <AccessTimeIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Next Refill Scheduled" 
                secondary={new Date(selectedDispenser.nextRefillAt).toLocaleString()} 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <BatteryFullIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Sensor Battery" 
                secondary={`${selectedDispenser.metrics.batteryLevel}%`} 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <SignalWifi4BarIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Sensor Status" 
                secondary={selectedDispenser.metrics.sensorStatus} 
              />
            </ListItem>
          </List>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" gutterBottom>
            System Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {allDispensersData.summary.totalDispensers}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Total Dispensers
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box textAlign="center">
                <Typography variant="h6" color="warning.main">
                  {allDispensersData.summary.needRefill}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Need Refill
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box textAlign="center">
                <Typography variant="h6" color="error.main">
                  {allDispensersData.summary.emptyDispensers}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Empty
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 7-day Consumption Chart */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <LocalLaundryServiceOutlinedIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Usage Trends
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            7-day paper towel consumption showing usage patterns and refill events.
          </Typography>
          <Box
            ref={chartRef}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '320px'
            }}
          />
        </CardContent>
      </Card>

      {/* Maintenance Alerts */}
      {(selectedDispenser.metrics.sensorStatus === 'Offline' || selectedDispenser.metrics.batteryLevel < 20) && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
              <WarningIcon sx={{ mr: 1, color: 'warning.main' }} />
              <Typography variant="h6">
                Maintenance Alerts
              </Typography>
            </Box>
            <List dense>
              {selectedDispenser.metrics.sensorStatus === 'Offline' && (
                <ListItem>
                  <ListItemIcon>
                    <WarningIcon color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Sensor Offline"
                    secondary="Dispenser sensor is not responding. Check connectivity and power."
                  />
                </ListItem>
              )}
              {selectedDispenser.metrics.batteryLevel < 20 && (
                <ListItem>
                  <ListItemIcon>
                    <WarningIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Low Battery"
                    secondary={`Battery level is ${selectedDispenser.metrics.batteryLevel}%. Replace battery soon.`}
                  />
                </ListItem>
              )}
            </List>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default PaperTowelEntityDrawer;


